import { StrictMode, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./i18n";
import { createBrowserRouter, RouterProvider, Navigate, useParams } from "react-router-dom";
import Layout from "./Layout.jsx";
import PagesLoading from "./Components/PagesLoading.jsx";
import { ContextProvider } from "./context.jsx";
import { NotificationProvider } from "./firebase/NotificationProvider.jsx"; // NEW
import TripInfo from "./Pages/TripInfo.jsx";
import EmployeeInfo from "./Pages/EmployeeInfo.jsx";
import Roles from "./Pages/Roles.jsx";
import { QueryClient, QueryClientProvider } from "react-query";
import ProtectedRoute from "./Components/ProtectedRoute.jsx";
import DisputesCenter from "./Pages/DisputesCenter.jsx";
// Lazy imports
const AllWallets = lazy(() => import("./Pages/AllWallets.jsx"));
const Dashboard = lazy(() => import("./Pages/Dashboard.jsx"));
const Accounts = lazy(() => import("./Pages/Accounts.jsx"));
const Vehicles = lazy(() => import("./Pages/Vehicles.jsx"));
const Trips = lazy(() => import("./Pages/Trips.jsx"));
const Employees = lazy(() => import("./Pages/Employees.jsx"));
const Finance = lazy(() => import("./Pages/Finance.jsx"));
const Advertising = lazy(() => import("./Pages/Advertising.jsx"));
const Rewards = lazy(() => import("./Pages/Rewards.jsx"));
const Insights = lazy(() => import("./Pages/Insights.jsx"));
const Incidents = lazy(() => import("./Pages/Incidents.jsx"));
const AuditLog = lazy(() => import("./Pages/AuditLog.jsx"));
const Login = lazy(() => import("./Pages/Login.jsx"));
const Settings = lazy(() => import("./Pages/Settings.jsx"));
const Profile = lazy(() => import("./Pages/Profile.jsx"));
const ProfileCreation = lazy(() => import("./Pages/ProfileCreation.jsx"));
const VehiclesRequests = lazy(() => import("./Pages/VehiclesRequests.jsx"));
const VehiclesInfo = lazy(() => import("./Pages/VehiclesInfo.jsx"));
const SystemWallet = lazy(() => import("./Pages/SystemWallet.jsx"));
const SupportCenter = lazy(() => import("./Pages/SupportCenter.jsx"));
const AppVersions = lazy(() => import("./Pages/AppVersions.jsx"));
const Configuration = lazy(() => import("./Pages/Configuration.jsx"));
const Occasions = lazy(() => import("./Pages/Occasions.jsx"));
const RewordsSection = lazy(() => import("./Pages/RewordsSection.jsx"));
const DistputDetails = lazy(() => import("./Pages/DistputDetails.jsx"));
const SupportDetails = lazy(() => import("./Pages/SupportDetails.jsx"));
const OnDemandOverview = lazy(() =>
  import("./Pages/OnDemand/OnDemandOverview.jsx"),
);
const OnDemandSettings = lazy(() =>
  import("./Pages/OnDemand/OnDemandSettings.jsx"),
);
const OnDemandRequests = lazy(() =>
  import("./Pages/OnDemand/OnDemandRequests.jsx"),
);
const OnDemandRequestDetails = lazy(() =>
  import("./Pages/OnDemand/OnDemandRequestDetails.jsx"),
);
const OnDemandReports = lazy(() =>
  import("./Pages/OnDemand/OnDemandReports.jsx"),
);
const OnDemandFinance = lazy(() =>
  import("./Pages/OnDemand/OnDemandFinance.jsx"),
);
const OnDemandFinanceTransactions = lazy(() =>
  import("./Pages/OnDemand/OnDemandFinanceTransactions.jsx"),
);
const OnDemandAuditLog = lazy(() =>
  import("./Pages/OnDemand/OnDemandAuditLog.jsx"),
);
const OnDemandAuditLogDetails = lazy(() =>
  import("./Pages/OnDemand/OnDemandAuditLogDetails.jsx"),
);
const OnDemandDrivers = lazy(() =>
  import("./Pages/OnDemand/OnDemandDrivers.jsx"),
);
const OnDemandDriverDetails = lazy(() =>
  import("./Pages/OnDemand/OnDemandDriverDetails.jsx"),
);
const OnDemandScheduledBookings = lazy(() =>
  import("./Pages/OnDemand/OnDemandScheduledBookings.jsx"),
);
const OnDemandScheduledBookingDetails = lazy(() =>
  import("./Pages/OnDemand/OnDemandScheduledBookingDetails.jsx"),
);
const OnDemandScheduledAnalytics = lazy(() =>
  import("./Pages/OnDemand/OnDemandScheduledAnalytics.jsx"),
);
const OnDemandScheduledAlerts = lazy(() =>
  import("./Pages/OnDemand/OnDemandScheduledAlerts.jsx"),
);
const OnDemandLiveOps = lazy(() =>
  import("./Pages/OnDemand/OnDemandLiveOps.jsx"),
);
const OnDemandAlerts = lazy(() =>
  import("./Pages/OnDemand/OnDemandAlerts.jsx"),
);
const OnDemandPlaceholder = lazy(() =>
  import("./Pages/OnDemand/OnDemandPlaceholder.jsx"),
);
const RewardsTransactions = lazy(() =>
  import("./Pages/RewardsTransactions.jsx")
);
const Withdrawals = lazy(() => import("./Pages/Withdrawals.jsx"));
const TargetedNotifications = lazy(() =>
  import("./Pages/TargetedNotifications.jsx"),
);
const TargetedNotificationCreate = lazy(() =>
  import("./Pages/TargetedNotificationCreate.jsx"),
);
const TargetedNotificationDetails = lazy(() =>
  import("./Pages/TargetedNotificationDetails.jsx"),
);
const queryClient = new QueryClient();

const RedirectOldDisputeDetails = () => {
  const { distputId } = useParams();
  return <Navigate to={`/disputes/details/${distputId}`} replace />;
};

const routes = createBrowserRouter([
  {
    path: "/",
    element: (
      <Suspense fallback={<PagesLoading />}>
        <Login />
      </Suspense>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/dashboard",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Dashboard />
          </Suspense>
        ),
      },
      {
        path: "/dashboard/setting",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Settings />
          </Suspense>
        ),
      },
      {
        path: "/accounts",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Accounts />
          </Suspense>
        ),
      },
      {
        path: "/accounts/profile/:profileId/:userType",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Profile />
          </Suspense>
        ),
      },
      {
        path: "/accounts/create-account/:accountId/:accountType",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <ProfileCreation />
          </Suspense>
        ),
      },
      {
        path: "/vehicles",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Vehicles />
          </Suspense>
        ),
      },
      {
        path: "/vehicles/requests",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <VehiclesRequests />
          </Suspense>
        ),
      },
      {
        path: "/vehicles/vehicle-info/:vehicleId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <VehiclesInfo />
          </Suspense>
        ),
      },
      {
        path: "/trips",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Trips />
          </Suspense>
        ),
      },
      {
        path: "/trips/trip-info/:tripId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <TripInfo />
          </Suspense>
        ),
      },
      {
        path: "/employees",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Employees />
          </Suspense>
        ),
      },
      {
        path: "/employees/employee-info/:employeeId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <EmployeeInfo />
          </Suspense>
        ),
      },
      {
        path: "employees/roles",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Roles />
          </Suspense>
        ),
      },
      {
        path: "/finance",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Finance />
          </Suspense>
        ),
      },
      {
        path: "/finance/wallet",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <SystemWallet />
          </Suspense>
        ),
      },
      {
        path: "/finance/all-wallets",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <AllWallets />
          </Suspense>
        ),
      },
      {
        path: "/finance/withdrawals",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Withdrawals />
          </Suspense>
        ),
      },
      {
        path: "/advertising",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Advertising />
          </Suspense>
        ),
      },
      {
        path: "/targeted-notifications",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <TargetedNotifications />
          </Suspense>
        ),
      },
      {
        path: "/targeted-notifications/new",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <TargetedNotificationCreate />
          </Suspense>
        ),
      },
      {
        path: "/targeted-notifications/:campaignId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <TargetedNotificationDetails />
          </Suspense>
        ),
      },
      {
        path: "/rewards",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Rewards />
          </Suspense>
        ),
      },
      {
        path: "/rewards/occasions",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Occasions />
          </Suspense>
        ),
      },
      {
        path: "/rewards/rewords-section",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <RewordsSection />
          </Suspense>
        ),
      },
      {
        path: "/rewards/rewards-transaction",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <RewardsTransactions />
          </Suspense>
        ),
      },
      {
        path: "/insights",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Insights />
          </Suspense>
        ),
      },
      {
        path: "/incidents",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Incidents />
          </Suspense>
        ),
      },
      {
        path: "/logs",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <AuditLog />
          </Suspense>
        ),
      },
      {
        path: "/support",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <SupportCenter />
          </Suspense>
        ),
      },
      {
        path: "/support/details/:supportId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <SupportDetails />
          </Suspense>
        ),
      },
      {
        path: "/disputes",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <DisputesCenter />
          </Suspense>
        ),
      },
      {
        path: "/disputes/details/:distputId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <DistputDetails />
          </Suspense>
        ),
      },
      {
        path: "/support/disputes",
        element: <Navigate to="/disputes" replace />,
      },
      {
        path: "/support/disputes/details/:distputId",
        element: <RedirectOldDisputeDetails />,
      },
      {
        path: "/dashboard/app-versions",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <AppVersions />
          </Suspense>
        ),
      },
      {
        path: "/config",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <Configuration />
          </Suspense>
        ),
      },
      {
        path: "/on-demand",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandOverview />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/live-ops",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandLiveOps />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/requests",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandRequests />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/requests/:requestId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandRequestDetails />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/drivers",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandDrivers />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/drivers/:driverId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandDriverDetails />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/scheduled",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandScheduledBookings />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/scheduled/analytics",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandScheduledAnalytics />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/scheduled/alerts",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandScheduledAlerts />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/scheduled/:bookingId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandScheduledBookingDetails />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/zones",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandPlaceholder title="Zones" />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/alerts",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandAlerts />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/reports",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandReports />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/finance",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandFinance />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/finance/transactions",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandFinanceTransactions />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/audit-log",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandAuditLog />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/audit-log/:auditId",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandAuditLogDetails />
          </Suspense>
        ),
      },
      {
        path: "/on-demand/settings",
        element: (
          <Suspense fallback={<PagesLoading />}>
            <OnDemandSettings />
          </Suspense>
        ),
      },
    ],
  },
]);

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ContextProvider>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <RouterProvider router={routes} />
        </NotificationProvider>
      </QueryClientProvider>
    </ContextProvider>
  </StrictMode>
);
